// Shared loading primitives — global-loading-skeleton.
//
// No new colours, spacing, or animation — every variant maps to an existing
// `.bl-*` class in styles/states.css, all token-built and pulsing on the shared
// @keyframes pulse. A page composes its own layout containers and drops
// `Skeleton`s into them; `LoadingState` is the accessible busy wrapper that
// carries the aria-busy / role=status / sr-only announcement once.
//
// The visual contract used to be the standalone BookingsSkeleton, which these
// primitives were consolidated from. That component is gone: a table now keeps
// its header mounted and swaps only the BODY for `line` bars on the real column
// grid, so the in-table skeleton is the contract (Claude, PR #150 — the note
// naming BookingsSkeleton outlived the component it described).

import type { ReactNode } from 'react';

// The pulse-block shapes the app already uses. Each is an existing `.bl-*`
// modifier (stat tile, table row, calendar cell, chart panel, text line) —
// adding a new one is a design decision, not a free call (a new visual token).
// `line` is a short text-height bar for a single table cell — it drops into the
// shared table grid so a loading body matches the real columns, header intact.
export type SkeletonVariant = 'stat' | 'row' | 'cell' | 'chart' | 'line';

export interface SkeletonProps {
  variant: SkeletonVariant;
}

// One pulsing placeholder block at a known height, so a loading layout reserves
// the same space the real content will occupy and nothing jumps when data lands.
export function Skeleton({ variant }: SkeletonProps) {
  return <div className={`bl-block bl-${variant}`} />;
}

export interface LoadingStateProps {
  /** Visually-hidden announcement for assistive tech, e.g. "Loading owners…". */
  label: string;
  /**
   * The layout container class for this surface's skeleton (e.g.
   * `bookings-loading`). Kept caller-supplied so each surface reuses its own
   * existing layout CSS rather than this primitive inventing a new one.
   */
  className: string;
  children: ReactNode;
}

// The accessible busy wrapper: marks the region aria-busy, exposes it as a
// status to assistive tech, and renders the sr-only announcement once. The
// skeleton blocks are passed as children so the wrapper stays layout-agnostic.
export function LoadingState({ label, className, children }: LoadingStateProps) {
  return (
    <div className={className} aria-busy="true" role="status">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
